import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { AttributionBackendAPIClient } from '@theme/attribution';
import { ThemeEvents, PartnerSelectedEvent, PartnerUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a Find A Brand Partner/Who Referred You component.
 *  
 * @typedef {object} Refs
 * @property {HTMLElement} searchContainer - The search container element.
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} resultsContainer - The search results container element.
 * @property {HTMLTemplateElement} resultTemplate - The template element for a single search result.
 * @property {HTMLElement} selectedResultContainer - The selected result container element.
 * @property {HTMLTemplateElement} selectedResultTemplate - The template element for the selected result.
 * @property {HTMLElement} submitButton - The set partner submit element.
 * @property {HTMLElement} errorContainer - The error container element.
 *
 * @extends {Component<Refs>}
 */
class ReferralFinder extends Component {
  requiredRefs = [
    'searchContainer',
    'searchInput',
    'resultsContainer',
    'resultTemplate',
    'selectedResultContainer',
    'selectedResultTemplate',
    'submitButton'
  ];

  /**
   * @type {AttributionBackendAPIClient}
   */
  backendClient = new AttributionBackendAPIClient();
  /** 
   * @type {AbortController | null} 
   */
  #activeFetch = null;
  /**
   * @type {{ type: string, resultId: string | null } | null}
   */
  #activeError = null;
  /** 
   * @type {DialogComponent | null} 
   */
  #dialog = null;
  /** 
   * @type {{ memberId: string, name: string } | null}
   */
  selectedPartner = null;

  get searchContainer() {
    return this.refs.searchContainer;
  }
  get searchInput() {
    return this.refs.searchInput;
  }
  get resultsContainer() {
    return this.refs.resultsContainer;
  }
  get selectedResultContainer() {
    return this.refs.selectedResultContainer;
  }
  get submitButton() {
    return this.refs.submitButton;
  }
  get searchQuery() {
    const queryValue = this.searchInput.value.trim();
    if (!queryValue) return null;

    const regex = /^\d+$/;
    const isNumericQuery = regex.test(queryValue);

    return {
      type: isNumericQuery ? 'memberId' : 'name',
      value: queryValue
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.partnerSelected, this.#handlePartnerSelected);
    
    this.searchByQueryType = this.dataset.searchByQueryType || 'member_id';
    this.#dialog = /**@type {DialogComponent} */ (this.closest('dialog-component'));
    if (this.#dialog) {
      this.#dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
      if (this.#hasURLAutoShowDialogContext()) this.#dialog.showDialog();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelSearchInProgress();
    this.removeEventListener(ThemeEvents.partnerSelected, this.#handlePartnerSelected);
    if (this.#dialog) {
      this.#dialog.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    }
  }

  /**
   * Debounced handler for search input. 
   */
  #handleSearchInput = debounce(this.#handleSearch.bind(this), 250);

  /**
   * Handles the search input event.
   */
  handleSearchInput() {
    if (!this.backendClient) return;

    this.#handleSearchInput();
  }

  /**
   * Handles the search reset click event.
   */
  handleSearchReset() {
    this.#cancelSearchInProgress();

    this.#clearError();
    this.#setSearchInputState({ state: null });

    this.searchInput.value = '';
    this.#clearResults();
  }

  /**
   * Handles the search input click event.
   */
  showResultsOnClick() {
    if (
      !this.searchQuery || 
      !this.resultsContainer.hasChildNodes() ||
      this.resultsContainer.classList.contains('is-active')
    ) return;

    this.#showResults();
  }

  /**
   * Handles the outside search input click event.
   * @param {Event} event - The click event.
   */
  hideResultsOnClickOutside(event) {
    if (
      !this.searchQuery || 
      !this.resultsContainer.hasChildNodes() ||
      !this.resultsContainer.classList.contains('is-active')
    ) return;

    const target = /** @type {Node} */ (event.target);
    if (!target) return;

    if (!this.searchContainer.contains(target)) this.#hideResults();
  }

  /**
   * Peforms name-based search or member ID lookup depending on query.
   */
  async #handleSearch() {
    this.#clearError();
    this.#clearResults();

    const query = this.searchQuery;
    if (!query) {
      this.#setSearchInputState({ state: null });
      return;
    }

    this.#setSearchInputState({ state: 'loading' });
    
    const results = await this.#getSearchResults(query);
    if (results === null) {
      this.#setSearchInputState({ state: null });
      return 
    };
    if (!results.length) {
      this.#activeError = {
        type: 'NoMembersFound',
        resultId: null
      };
      this.#showError(this.#activeError.type);
      this.#setSearchInputState({ state: 'error' });
      return;
    }

    this.#setSearchInputState({ state: null });
    this.#buildSearchResults(results);
  }

  /**
   * @param {any} query - The search query.
   */
  async #getSearchResults(query) {
    const abortController = this.#createAbortController();
    let results = /** @type {{ memberId: string, name: string }[]} */ ([]);

    try {
      const searchByMemberId =
        (
          this.searchByQueryType === 'member_id' || 
          this.searchByQueryType === 'member_id_or_name'
        ) && query.type === 'memberId';
      const searchByName = 
        (
          this.searchByQueryType === 'name' || 
          this.searchByQueryType === 'member_id_or_name'
        ) && query.type === 'name';

      if (searchByMemberId || searchByName) {
        const searchData = await this.backendClient.getPartnerSearch(query.value, 5);
        if (abortController.signal.aborted) return null;

        const partners = searchData?.members;
        if (partners?.length) {
          results = partners.map((/** @type {any} */ partner) => ({ 
            memberId: partner.memberId, 
            name: partner.contactName 
          }));
        }
      }
    } catch (e) {
      console.error(e);
    }

    return results;
  }

  /**
   * Builds the search results to render.
   * @param {{ name: string, memberId: string }[]} results
   */
  #buildSearchResults(results) {
    this.#clearResults();

    if (!results.length) return;

    const { resultTemplate } = this.refs;
    const fragment = document.createDocumentFragment();

    results.forEach((result) => {
      const clone = this.#cloneResultFragment(resultTemplate, result);
      const resultElem = /** @type {HTMLElement} */ (clone.firstElementChild);
      resultElem.addEventListener('click', (event) => { this.handleSelectResult(event, result); });
      resultElem.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handleSelectResult(event, result);
        }
      });

      fragment.appendChild(clone);
    });

    this.#renderResults(fragment);
  }

  /**
   * Handles the select result (to set as partner) click event.
   * @param {Event} event - The click event.
   * @param {{ memberId: string, name: string }} result - The result to select.
   */
  async handleSelectResult(event, result) {
    event.stopPropagation();

    this.#clearError();
    this.#setSearchInputState({ state: null });

    this.dispatchEvent(new PartnerSelectedEvent(null));

    this.#autocompleteSearch(result);
    this.#buildSelectedResult(result);
    this.#hideResults();

    this.dispatchEvent(new PartnerSelectedEvent(result));
    
    this.#setSearchInputState({ state: 'success' });
  }

  /**
   * Builds the selected result to render.
   * @param {{ memberId: string, name: string }} result
   */
  #buildSelectedResult(result) {
    this.#clearSelectedResult();

    const { selectedResultTemplate } = this.refs;
    const clone = this.#cloneResultFragment(selectedResultTemplate, result);
    const removeSelectedResultBtn = /** @type {HTMLElement} */ (clone.querySelector('[data-referral-finder-remove-selected-result]'));
    removeSelectedResultBtn.addEventListener('click', (event) => { this.handleRemoveSelectedResult(event, result); });
      
    this.#renderSelectedResult(clone);
  }

  /**
   * Handles the remove selected result (to set as partner) click event.
   * @param {Event} event - The click event.
   * @param {{ memberId: string, name: string }} result - The selected result to remove.
   */
  handleRemoveSelectedResult(event, result) {
    event.stopPropagation();

    if (this.#activeFetch) this.#activeFetch.abort();

    this.dispatchEvent(new PartnerSelectedEvent(null));

    this.#clearSelectedResult();

    if (!this.#activeError) {
      this.#setSearchInputState({ state: null });
    }
    if (this.#activeError?.resultId === result.memberId) {
      this.#clearError();
      this.#setSearchInputState({ state: null });
    }
  }

  /**
   * Handles the set partner click event.
   * @param {Event} event - The click event.
   */
  async handleSetPartner(event) {
    event.stopPropagation();

    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
    if (!this.selectedPartner) return;

    document.dispatchEvent(new PartnerUpdateEvent(this.selectedPartner.memberId));
    
    this.#closeCurrentDialog();
  }

  /**
   * Handles the remove partner click event.
   * @param {Event} event - The click event.
   */
  handleRemovePartner(event) {
    document.dispatchEvent(new PartnerUpdateEvent(null));

    this.#closeCurrentDialog();
  }

  /**
   * Handles the partner selected event.
   * @param {PartnerSelectedEvent} event - The partner selected event.
   */
  #handlePartnerSelected = (event) => {
    this.#setSubmitButtonDisabledState({ disabled: true });
      
    this.selectedPartner = event.detail.partner;
    if (this.selectedPartner) {
      this.#setSubmitButtonDisabledState({ disabled: false });
    }
  }

  /**
   * Autocompletes search input based on selected result.
   * @param {{ memberId: string, name: string }} result - The selected result data.
   */
  #autocompleteSearch(result) {
    let autocomplete = null;

    const useMemberId =
      (
        this.searchByQueryType === 'member_id' || 
        this.searchByQueryType === 'member_id_or_name'
      ) && this.searchQuery?.type === 'memberId';
    const useName = 
      (
        this.searchByQueryType === 'name' || 
        this.searchByQueryType === 'member_id_or_name'
      ) && this.searchQuery?.type === 'name';
    
    if (useMemberId) autocomplete = result.memberId;
    else if (useName) autocomplete = result.name;

    if (autocomplete) this.searchInput.value = autocomplete;
  }

  /**
   * Creates an abort controller.
   */
  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Cancels search in progress.
   */
  #cancelSearchInProgress() {
    this.#handleSearchInput.cancel();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
  }

  /**
   * Clones a template and populates it with the result data.
   * @param {HTMLTemplateElement} template - The template.
   * @param {{ memberId: string, name: string }} result - The result data.
   */
  #cloneResultFragment(template, result) {
    const clone = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const resultElem = /** @type {HTMLElement} */ (clone.firstElementChild);

    const nameElem = resultElem.querySelector('[data-referral-finder-result-name]');
    const memberIdElem = resultElem.querySelector('[data-referral-finder-result-id]');

    if (nameElem) nameElem.textContent = result.name;
    if (memberIdElem) memberIdElem.textContent = result.memberId;

    return clone;
  }

  /**
   * Clears the search results.
   */
  #clearResults() {
    this.resultsContainer.innerHTML = '';
    this.#hideResults();
  }

  /**
   * Renders the search results.
   * @param {DocumentFragment} fragment - The search results fragment.
   */
  #renderResults(fragment) {
    this.resultsContainer.appendChild(fragment);
    this.#showResults();
  }

  /**
   * Hides the search results.
   */
  #hideResults() {
    this.resultsContainer.classList.remove('is-active');
  }

  /**
   * Shows the search results.
   */
  #showResults() {
    this.resultsContainer.classList.add('is-active');
  }

  /**
   * Clears the selected result.
   */
  #clearSelectedResult() {
    this.selectedResultContainer.innerHTML = '';
    this.#hideSelectedResult();
  }

  /**
   * Renders the selected result.
   * @param {DocumentFragment} fragment - The selected result fragment.
   */
  #renderSelectedResult(fragment) {
    this.selectedResultContainer.appendChild(fragment);
    this.#showSelectedResult();
  }

  /**
   * Hides the selected result.
   */
  #hideSelectedResult() {
    this.selectedResultContainer.classList.remove('is-active');
  }

  /**
   * Shows the selected result.
   */
  #showSelectedResult() {
    this.selectedResultContainer.classList.add('is-active');
  }

  /**
   * Hides error.
   */
  #hideError() {
    const { errorContainer } = this.refs;
    if (!errorContainer) return;

    const errorMessages = errorContainer.querySelectorAll('[data-error]');
    errorMessages.forEach((message) => message.classList.add('hidden'));
    errorContainer.classList.add('hidden');
  }

  /**
   * Shows error.
   * @param {string} type - The type of error.
   */
  #showError(type) {
    const { errorContainer } = this.refs;
    if (!errorContainer) return;
    
    this.#hideError();
    
    const targetErrorMessage = errorContainer.querySelector(`[data-error="${type}"]`);
    if (targetErrorMessage) {
      targetErrorMessage.classList.remove('hidden');
      errorContainer.classList.remove('hidden');
    }
  }

  /**
   * Clears error.
   */
  #clearError() {
    this.#activeError = null;
    this.#hideError();
  }

  /**
   * Sets the state of the search input.
   * @param {{ state: string | null }} _state - The state.
   */
  #setSearchInputState({ state }) {
    this.searchInput.classList.remove('has-loading', 'has-error', 'has-success');

    switch (state) {
      case 'loading': 
        this.searchInput.classList.add('has-loading');
        break;
      case 'error':
        this.searchInput.classList.add('has-error');
        break;
      case 'success':
        this.searchInput.classList.add('has-success');
        break;
      default:
        break;
    }
  }
  
  /**
   * Sets the disabled/enabled state of the submit button.
   * @param {{ disabled: boolean }} state - The disabled/enabled state.
   */
  #setSubmitButtonDisabledState({ disabled }) {
    this.submitButton.setAttribute('aria-disabled', String(disabled));
    this.submitButton.classList.toggle('disabled', disabled);
  }

  /**
   * Sets the loading/not loading state of the submit button.
   * @param {{ loading: boolean }} state - The loading/not loading state.
   */
  #setSubmitButtonLoadingState({ loading }) {
    this.submitButton.classList.toggle('loading', loading);
  }
  
  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#reset();
  }

  /**
   * Closes the current dialog.
   */
  #closeCurrentDialog() {
    if (this.#dialog) this.#dialog.closeDialog();
  }

  /**
   * Gets whether or not the URL contains context to automatically show current dialog.
   */
  #hasURLAutoShowDialogContext() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('attribution');
  }

  /**
   * Clears URL of context to automatically show current dialog.
   */
  #clearURLAutoShowDialogContext() {
    const url = new URL(window.location.href);
    url.searchParams.delete('attribution');
    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Resets all.
   */
  #reset() {
    this.searchInput.value = '';

    this.dispatchEvent(new PartnerSelectedEvent(null));

    this.#cancelSearchInProgress();
    this.#clearResults();
    this.#clearSelectedResult();
    
    this.#clearError();
    this.#setSearchInputState({ state: null });
    this.#setSubmitButtonDisabledState({ disabled: true });

    if (this.#hasURLAutoShowDialogContext()) this.#clearURLAutoShowDialogContext();
  }
}

if (!customElements.get('referral-finder-component')) {
  customElements.define('referral-finder-component', ReferralFinder);
}